// Dates, times, numbers, money and durations in the active language, all through Intl. The
// formatters are cached per language; they are called while rendering, after useT() has
// subscribed the component to language changes.
import { LOCALE_INFO, type Locale } from "./locales";
import { currentLocale } from "./store";

const cache = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat | Intl.RelativeTimeFormat>();

function cached<F extends Intl.DateTimeFormat | Intl.NumberFormat | Intl.RelativeTimeFormat>(key: string, make: (tag: string, locale: Locale) => F): F {
  const locale = currentLocale();
  const id = `${locale}:${key}`;
  let f = cache.get(id);
  if (!f) {
    f = make(LOCALE_INFO[locale].intl, locale);
    cache.set(id, f);
  }
  return f as F;
}

const dateTime = (key: string, options: Intl.DateTimeFormatOptions) => cached(key, (tag) => new Intl.DateTimeFormat(tag, options));
const numberFormat = (key: string, options: Intl.NumberFormatOptions) => cached(key, (tag) => new Intl.NumberFormat(tag, options));

/** Date and time, "12/09/2026, 14:03:21" in English. */
export function formatDateTime(ms: number): string {
  return dateTime("datetime", { dateStyle: "short", timeStyle: "medium" }).format(ms);
}

/** "14:03". */
export function formatHourMinute(ms: number): string {
  return dateTime("hm", { hour: "2-digit", minute: "2-digit" }).format(ms);
}

/** "14:03:21". */
export function formatClock(ms: number): string {
  return dateTime("hms", { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(ms);
}

/** English keeps three-letter months: ICU's en-GB writes "Sept". */
function shortMonths(parts: Intl.DateTimeFormatPart[]): string {
  const english = currentLocale() === "en";
  return parts.map((p) => (english && p.type === "month" ? p.value.slice(0, 3) : p.value)).join("");
}

/** "12 Sep", "12 sept.", "12 sept". */
export function formatDayMonth(date: Date | number): string {
  return shortMonths(dateTime("dm", { day: "numeric", month: "short" }).formatToParts(date));
}

/** "12 Sep 2026". */
export function formatDay(date: Date | number): string {
  return shortMonths(dateTime("dmy", { day: "numeric", month: "short", year: "numeric" }).formatToParts(date));
}

export function formatNumber(n: number, options: Intl.NumberFormatOptions = {}): string {
  return numberFormat(JSON.stringify(options), options).format(n);
}

/** 4210 → "4.2k", "4,2 k", "4,2 mil". */
export function formatCompact(n: number): string {
  return numberFormat("compact", { notation: "compact", maximumFractionDigits: 1 }).format(n);
}

/** US dollars, to the cent unless `digits` says otherwise. */
export function formatUsd(n: number, digits = 2): string {
  return cached(`usd${digits}`, (tag, locale) =>
    new Intl.NumberFormat(tag, {
      style: "currency",
      currency: "USD",
      currencyDisplay: LOCALE_INFO[locale].currencyDisplay,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }),
  ).format(n);
}

type Unit = "day" | "hour" | "minute" | "second";

/** A number with a unit, "3h", "3 h", "45s"; `pad` writes two digits ("08s"). */
export function formatUnit(n: number, unit: Unit, display: "narrow" | "short" | "long" = "narrow", pad = false): string {
  return numberFormat(`${unit}:${display}:${pad}`, { style: "unit", unit, unitDisplay: display, minimumIntegerDigits: pad ? 2 : 1 }).format(n);
}

/** "now", "4 min ago", "3 hr ago", "yesterday", "5 days ago", in the language's own words. */
export function formatAgo(ms: number, now = Date.now()): string {
  const rtf = cached("ago", (tag) => new Intl.RelativeTimeFormat(tag, { numeric: "auto", style: "short" }));
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 60) return rtf.format(0, "second");
  if (s < 3600) return rtf.format(-Math.floor(s / 60), "minute");
  if (s < 86_400) return rtf.format(-Math.floor(s / 3600), "hour");
  return rtf.format(-Math.floor(s / 86_400), "day");
}
